import React, { useState, useEffect } from "react";
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Alert,
} from "react-native";
import { X, Upload } from "lucide-react";
import { autoSyncWithIntegration } from "../../../../services/api-integrations";
import { updatePatientNote } from "../../../../services/api-documentation";

import { mixpanel } from "../../../../utils/mixpanel";
import { logUIError } from "../../../../services/api-misc";

interface EMRSyncModalProps {
  visible: boolean;
  onClose: () => void;
  noteId: string;
  appointmentId: number;
  noteJson: any;
  originalNoteText: string;
  session: any;
  patient: any;
  onSyncSuccess?: () => void;
}

interface EditableSection {
  [key: string]: any;
}

const EMRSyncModal: React.FC<EMRSyncModalProps> = ({
  visible,
  onClose,
  noteId,
  appointmentId,
  noteJson,
  originalNoteText,
  session,
  patient,
  onSyncSuccess,
}) => {
  const [editableData, setEditableData] = useState<EditableSection>({});
  const [syncing, setSyncing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const logEMRSyncUIError = async (
    message: string,
    extraContext?: Record<string, unknown>,
  ) => {
    try {
      await logUIError({
        message,
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        context: {
          component: "EMRSyncModal",
          patientId: patient?.clinicOSUUID,
          patientClinicOSUUID: patient?.clinicOSUUID,
          noteId,
          appointmentId,
          hadChanges: hasChanges,
          ...extraContext,
        },
      });
    } catch (err) {
      console.warn("logEMRSyncUIError failed", err);
    }
  };

  // Validate required fields before proceeding
  if (!noteId || !appointmentId) {
    throw new Error("Missing required note ID or appointment ID");
  }

  if (!session?.clinicId || !patient?.clinicOSUUID) {
    throw new Error("Missing clinic ID or patient UUID");
  }
  // Initialize editable data when modal opens or noteJson changes
  useEffect(() => {
    if (visible && noteJson) {
      setEditableData(JSON.parse(JSON.stringify(noteJson))); // Deep copy
      setHasChanges(false);
    }
  }, [visible, noteJson]);

  const handleClose = () => {
    if (hasChanges) {
      Alert.alert(
        "Unsaved Changes",
        "You have unsaved changes. Are you sure you want to close?",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Close",
            style: "destructive",
            onPress: () => {
              setHasChanges(false);
              onClose();
            },
          },
        ],
      );
    } else {
      onClose();
    }
  };

  const updateNestedValue = (path: string[], value: string) => {
    setEditableData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;

      // Navigate to the parent object
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }

      // Set the value
      current[path[path.length - 1]] = value;
      setHasChanges(true);
      return newData;
    });
  };

  const renderEditableField = (
    key: string,
    value: any,
    path: string[] = [],
  ): React.ReactNode => {
    const currentPath = [...path, key];
    const pathString = currentPath.join(".");

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      // Render nested object
      return (
        <View key={pathString} style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>
            {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")}
          </Text>
          <View style={styles.nestedContainer}>
            {Object.entries(value).map(([nestedKey, nestedValue]) =>
              renderEditableField(nestedKey, nestedValue, currentPath),
            )}
          </View>
        </View>
      );
    }

    if (Array.isArray(value)) {
      // Handle arrays - display as JSON string for simplicity
      return (
        <View key={pathString} style={styles.fieldContainer}>
          <Text style={styles.fieldLabel}>
            {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")}
          </Text>
          <Text style={styles.arrayValue}>
            {JSON.stringify(value, null, 2)}
          </Text>
        </View>
      );
    }

    // Handle primitive values (string, number, boolean)
    const stringValue = value?.toString() || "";
    const isLongText = stringValue.length > 50;

    return (
      <View key={pathString} style={styles.fieldContainer}>
        <Text style={styles.fieldLabel}>
          {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")}
        </Text>
        <TextInput
          style={[
            styles.fieldInput,
            isLongText ? styles.multilineInput : styles.singleLineInput,
          ]}
          value={stringValue}
          onChangeText={(text) => updateNestedValue(currentPath, text)}
          multiline={isLongText}
          numberOfLines={isLongText ? 4 : 1}
          placeholder={`Enter ${key.replace(/_/g, " ")}`}
        />
      </View>
    );
  };

  const handleSyncToEMR = async () => {
    try {
      setSyncing(true);

      // Track sync attempt
      mixpanel.track("EMR Sync Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Note ID": noteId,
        "Appointment ID": appointmentId,
        "Has Changes": hasChanges,
      });

      // Step 1: Update the note if there are changes
      if (hasChanges) {
        console.log("Updating note with edited data before sync...");

        // Prepare the payload with updated data
        const updatePayload = {
          note: originalNoteText, // Keep original text format
          note_json: editableData, // Use the edited JSON data
          // do NOT include audio_ids unless you want to change them
        };

        // Update the note with the edited data
        await updatePatientNote(
          session,
          session?.clinicId,
          patient?.clinicOSUUID,
          parseInt(noteId),
          updatePayload,
        );

        console.log("Note updated successfully with edited data");

        // Track note update
        mixpanel.track("Note Updated Before EMR Sync", {
          "User Email": session?.userEmail,
          "Clinic Name": session?.clinicName,
          "Patient ID": patient?.clinicOSUUID,
          "Patient Name": patient?.name,
          "Note ID": noteId,
        });
      }

      // Step 2: Call the auto-sync endpoint
      console.log("Proceeding with EMR auto-sync...");
      await autoSyncWithIntegration(session, {
        note_id: parseInt(noteId),
        appointment_id: appointmentId,
      });

      // Track success
      mixpanel.track("EMR Sync Completed", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Note ID": noteId,
        "Appointment ID": appointmentId,
        "Had Changes": hasChanges,
      });

      Alert.alert(
        "Success",
        hasChanges
          ? "Note updated and synced to EMR successfully"
          : "Note synced to EMR successfully",
        [
          {
            text: "OK",
            onPress: () => {
              onSyncSuccess?.();
              onClose();
            },
          },
        ],
      );
    } catch (error) {
      console.error("Error syncing to EMR:", error);

      // Track error with more context
      mixpanel.track("EMR Sync Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Note ID": noteId,
        "Appointment ID": appointmentId,
        "Had Changes": hasChanges,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
        "Error Type":
          error instanceof Error ? error.constructor.name : "Unknown",
      });

      logEMRSyncUIError("EMR sync failed", {
        error: error instanceof Error ? error.message : String(error),
      });

      // Provide more specific error messages
      let errorMessage = "Unknown error occurred";
      if (error instanceof Error) {
        if (error.message.includes("Failed to update note")) {
          errorMessage =
            "Failed to save note changes. Please check your connection and try again.";
        } else if (error.message.includes("sync")) {
          errorMessage =
            "Note was saved but failed to sync to EMR. You can try syncing again.";
        } else {
          errorMessage = error.message;
        }
      }

      Alert.alert("Sync Failed", `${errorMessage}\n\nPlease try again.`);
    } finally {
      setSyncing(false);
    }
  };

  if (!noteJson) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={handleClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Text style={styles.modalTitle}>Sync to EMR</Text>
              <Text style={styles.modalSubtitle}>
                Note ID: {noteId} • Appointment ID: {appointmentId}
              </Text>
            </View>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={handleClose}
              disabled={syncing}
            >
              <X size={24} color="#666666" />
            </TouchableOpacity>
          </View>

          {/* Scrollable content */}
          <ScrollView
            style={styles.scrollContainer}
            showsVerticalScrollIndicator={true}
          >
            <View style={styles.contentContainer}>
              {Object.entries(editableData)
                .filter(([key, value]) => key !== "metadata") // Filter out metadata
                .map(([key, value]) => renderEditableField(key, value))}
            </View>
          </ScrollView>

          {/* Footer with action buttons */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={handleClose}
              disabled={syncing}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.syncButton, syncing && styles.disabledButton]}
              onPress={handleSyncToEMR}
              disabled={syncing}
            >
              {syncing ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <>
                  <Upload size={16} color="#FFFFFF" />
                  <Text style={styles.syncButtonText}>Sync to EMR</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    margin: 20,
    maxHeight: "90%",
    width: "90%",
    maxWidth: 600,
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
  },
  headerLeft: {
    flex: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 14,
    color: "#666666",
  },
  closeButton: {
    padding: 4,
    marginLeft: 16,
  },
  warningContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF8E1",
    padding: 12,
    marginHorizontal: 20,
    marginTop: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#FF9800",
  },
  warningText: {
    fontSize: 14,
    color: "#E65100",
    marginLeft: 8,
    flex: 1,
  },
  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  sectionContainer: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
  },
  nestedContainer: {
    paddingLeft: 16,
  },
  fieldContainer: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#555555",
    marginBottom: 6,
  },
  fieldInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDDDDD",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: "#333333",
  },
  singleLineInput: {
    height: 44,
  },
  multilineInput: {
    minHeight: 88,
    textAlignVertical: "top",
  },
  arrayValue: {
    backgroundColor: "#F5F5F5",
    borderRadius: 8,
    padding: 12,
    fontSize: 12,
    color: "#666666",
    fontFamily: "monospace",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#EEEEEE",
    backgroundColor: "#FAFAFA",
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: "#F2F2F2",
    marginRight: 12,
  },
  cancelButtonText: {
    fontSize: 14,
    color: "#555555",
    fontWeight: "500",
  },
  syncButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: "#0085FF",
  },
  syncButtonText: {
    fontSize: 14,
    color: "#FFFFFF",
    fontWeight: "500",
    marginLeft: 8,
  },
  disabledButton: {
    opacity: 0.7,
  },
});

export default EMRSyncModal;
